import {
  Cell,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  Header,
  HeaderGroup,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AddPlus2, ErrorWarningtraiangle, More, Search } from '../../../assets/icons';
import { RoutePath } from '../../../routes';
import {
  useDeleteChargerModelMutation,
  useFetchChargerModelDeploymentQuery,
} from '../../../services/chargerModels.api';
import { ChargerModelDeployment } from '../../../stores/types/chargerModels.interface';
import {
  ButtonSize,
  ButtonType,
  ColorType,
  Label,
  LabelType,
  Menu,
  MenuItem,
  MODAL_TYPES,
  Pill,
  useGlobalModalContext,
} from '../../_ui';
import { Button } from '../../_ui/Button.component';
import { Card } from '../../_ui/Card.component';
import { FormInput } from '../../_ui/Input.component';
import { GridPager } from '../../_ui/grid/Grid-Pager.component';

const ROWS_PER_PAGE = 15;

/** Table row built from GET /internal/core/v2/chargerModels (deployment list). */
type ChargerModelListRow = ChargerModelDeployment & {
  vendor: string;
  chargermodelRecordId: string | null;
};

const columnHelper = createColumnHelper<ChargerModelListRow>();

const LEVEL_DISPLAY_MAP: Record<string, string> = {
  DC_FAST: 'DC Fast',
  LEVEL_2: 'AC Level 2',
  LEVEL_1: 'AC Level 1',
};

const CONNECTOR_TYPE_DISPLAY_MAP: Record<string, string> = {
  CCS_1: 'CSS',
  CCS_2: 'CSS2',
  TESLA: 'NACS',
  CHADEMO: 'CHAdeMO',
  TYPE_1: 'Type 1',
  TYPE_2: 'Type 2',
  NACS: 'NACS',
};

const formatLevel = (level: string) => LEVEL_DISPLAY_MAP[level] ?? level;

const formatConnectorType = (type: string) => CONNECTOR_TYPE_DISPLAY_MAP[type] ?? type;

const uniqueConnectorTypesFromDeployment = (types: string[]): string[] =>
  Array.from(new Set(types.filter(Boolean)));

const ChargerModelRowMenu = memo(({ row }: { row: ChargerModelListRow }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showModal } = useGlobalModalContext();
  const searchSuffix = typeof window !== 'undefined' ? window.location.search : '';

  const canDelete = row.numberOfChargers === 0 && Boolean(row.chargermodelRecordId);

  const handleDelete = () => {
    if (!row.chargermodelRecordId) {
      return;
    }
    showModal(MODAL_TYPES.ALERT_MODAL, {
      title: t('support_section.charger_models_delete_title'),
      message: t('support_section.charger_models_delete_message', {
        vendor: row.vendor,
        model: row.model,
      }),
      icon: ErrorWarningtraiangle,
      iconColor: ColorType.NEGATIVE,
      width: '400px',
      height: 'max-content',
      mutationHook: useDeleteChargerModelMutation,
      mutationParams: row.chargermodelRecordId,
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
        },
        {
          label: t('delete'),
          type: ButtonType.DESTRUCTIVE,
          size: ButtonSize.SMALL,
          isSubmit: true,
        },
      ],
      shouldCloseOnOverlayClick: false,
    });
  };

  return (
    <div className='flex justify-end pr-1'>
      <Menu
        placement='bottom-end'
        icon={More}
        iconAlt={t('icon_alt_more_options')}
        mixWidth={220}
        contentClassName='!rounded !p-2 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.1)]'
        dataTestId='charger-model-row-menu'
      >
        <MenuItem
          label={t('support_section.charger_models_menu_edit')}
          onClick={() => navigate(`/${RoutePath.CHARGER_MODELS}/${row.id}${searchSuffix}`)}
        />
        <MenuItem
          label={t('support_section.charger_models_menu_duplicate')}
          onClick={() =>
            navigate(`/${RoutePath.CHARGER_MODELS}/new${searchSuffix}`, {
              state: { duplicateFromId: row.id },
            })
          }
        />
        {canDelete && (
          <MenuItem
            label={t('support_section.charger_models_menu_delete')}
            labelColor={ColorType.NEGATIVE}
            onClick={handleDelete}
          />
        )}
      </Menu>
    </div>
  );
});

ChargerModelRowMenu.displayName = 'ChargerModelRowMenu';

export const ChargerModels = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const searchSuffix = typeof window !== 'undefined' ? window.location.search : '';
  const {
    data: deploymentRows = [],
    isLoading,
    isError,
  } = useFetchChargerModelDeploymentQuery(undefined, { refetchOnMountOrArgChange: true });

  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const modelsWithDeployment = useMemo((): ChargerModelListRow[] => {
    return deploymentRows
      .toSorted((a, b) =>
        `${a.manufacturer}-${a.model}`.localeCompare(`${b.manufacturer}-${b.model}`),
      )
      .map((row) => ({
        ...row,
        vendor: row.manufacturer,
        chargermodelRecordId: row.id,
      }));
  }, [deploymentRows]);

  const filteredModels = useMemo(() => {
    if (!searchText.trim()) {
      return modelsWithDeployment;
    }
    const query = searchText.toLowerCase().trim();
    return modelsWithDeployment.filter(
      (m) => m.vendor.toLowerCase().includes(query) || m.model.toLowerCase().includes(query),
    );
  }, [modelsWithDeployment, searchText]);

  const totalPages = Math.max(1, Math.ceil(filteredModels.length / ROWS_PER_PAGE));

  const paginatedModels = useMemo(() => {
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    return filteredModels.slice(start, start + ROWS_PER_PAGE);
  }, [filteredModels, currentPage]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const columns = [
    columnHelper.accessor('vendor', {
      header: 'Manufacturer',
      cell: (info: { getValue: () => string }) => {
        const v = info.getValue();
        return (
          <span className='block w-full min-w-0 truncate' title={v}>
            {v}
          </span>
        );
      },
      size: 250,
    }),
    columnHelper.accessor('model', {
      header: 'Model',
      cell: (info: { getValue: () => string }) => {
        const v = info.getValue();
        return (
          <span className='block w-full min-w-0 truncate' title={v}>
            {v}
          </span>
        );
      },
      size: 250,
    }),
    columnHelper.accessor('level', {
      header: 'Level',
      cell: (info: { getValue: () => string }) => formatLevel(info.getValue()),
      size: 106,
    }),
    columnHelper.display({
      id: 'connectors',
      header: 'Connector(s)',
      cell: ({ row }: { row: { original: ChargerModelListRow } }) => {
        const types = uniqueConnectorTypesFromDeployment(row.original.connectorTypes);
        return (
          <div className='flex flex-wrap gap-1 opacity-80'>
            {types.map((type) => (
              <Pill key={type} label={formatConnectorType(type)} />
            ))}
          </div>
        );
      },
      size: 163,
    }),
    columnHelper.accessor('maxWattage', {
      header: 'Max wattage',
      cell: (info: { getValue: () => number }) => info.getValue(),
      size: 141,
    }),
    columnHelper.accessor('maxAmperage', {
      header: 'Max amperage',
      cell: (info: { getValue: () => number }) => `${info.getValue()}A`,
      size: 146,
    }),
    columnHelper.display({
      id: 'deployed',
      header: 'Deployed',
      cell: ({ row }: { row: { original: ChargerModelListRow } }) => {
        const n = row.original.numberOfChargers;
        return (
          <span>
            {n} {n === 1 ? 'charger' : 'chargers'}
          </span>
        );
      },
      size: 119,
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: ({ row }: { row: { original: ChargerModelListRow } }) => (
        <ChargerModelRowMenu row={row.original} />
      ),
      size: 50,
    }),
  ];

  const table = useReactTable({
    data: paginatedModels,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <Label text={t('loading')} type={LabelType.BODY2} color={ColorType.GREY5} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex items-center justify-center py-20'>
        <Label
          text='Failed to load charger models'
          type={LabelType.BODY2}
          color={ColorType.NEGATIVE}
        />
      </div>
    );
  }

  return (
    <div className='p-6'>
      <Card>
        <div className='flex items-center justify-between pb-4'>
          <div>
            <span className='text-xl font-semibold text-black leading-7'>Charger models </span>
            <span className='text-sm font-medium text-grey5 leading-5'>
              (showing {filteredModels.length})
            </span>
          </div>
          <Button
            icon={AddPlus2}
            label='Add charger model'
            size={ButtonSize.SMALL}
            type={ButtonType.TERTIARY}
            onClick={() => navigate(`/${RoutePath.CHARGER_MODELS}/new${searchSuffix}`)}
          />
        </div>

        <div className='flex items-center pt-4 pb-8 w-full max-w-2xl'>
          <FormInput
            defaultValue={searchText}
            onChange={handleSearchChange}
            icon={Search}
            placeholder='Filter by manufacturer, model'
            iconAlt='Search'
            width='100%'
          />
        </div>

        <div className='overflow-x-auto overflow-y-visible'>
          <table className='border-collapse table-fixed w-full' style={{ minWidth: '100%' }}>
            <thead>
              {table.getHeaderGroups().map((headerGroup: HeaderGroup<ChargerModelListRow>) => (
                <tr
                  key={headerGroup.id}
                  className='bg-white'
                  style={{
                    boxShadow: 'inset 0px 1px 0px 0px grey2, inset 0px -1px 0px 0px grey2',
                  }}
                >
                  {headerGroup.headers.map((header: Header<ChargerModelListRow, unknown>) => (
                    <th
                      key={header.id}
                      style={{
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize ?? 40,
                      }}
                      className='pl-3 py-2.5 text-left text-sm font-semibold text-grey6 h-10'
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {paginatedModels.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className='text-center py-20'>
                    <Label
                      text='No charger models found'
                      type={LabelType.BODY2}
                      color={ColorType.GREY5}
                    />
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row: Row<ChargerModelListRow>) => (
                  <tr key={row.id} className='hover:bg-grey1 bg-white min-h-10'>
                    {row.getVisibleCells().map((cell: Cell<ChargerModelListRow, unknown>) => {
                      const isConnectors = cell.column.id === 'connectors';
                      const isVendorOrModel =
                        cell.column.id === 'vendor' || cell.column.id === 'model';
                      return (
                        <td
                          key={cell.id}
                          style={{
                            width: cell.column.getSize(),
                            maxWidth: cell.column.getSize(),
                          }}
                          className={
                            isConnectors
                              ? 'pl-3 py-2.5 text-sm text-black whitespace-normal align-middle min-w-0'
                              : isVendorOrModel
                              ? 'pl-3 py-2.5 text-sm text-black align-middle min-w-0 overflow-hidden'
                              : 'pl-3 py-2.5 text-sm text-black overflow-hidden whitespace-nowrap'
                          }
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredModels.length > ROWS_PER_PAGE && (
          <GridPager
            totalPage={totalPages}
            currentPage={currentPage}
            loadPage={handlePageChange}
            showPageSearch={true}
          />
        )}
      </Card>
    </div>
  );
};
